import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { forgetGame, loadMyGames, type MyGame } from '../lib/myGames';

/** A row is a remembered game plus whatever the server currently says about it. */
interface Row extends MyGame {
  status?: string;
  playerCount?: number;
  /** Server says it's gone; kept with a Remove button so it doesn't look lost. */
  missing?: boolean;
}

/**
 * Games this browser created or hosted: renders from localStorage immediately, then
 * refreshes each against the server.
 */
export function MyGamesList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>(loadMyGames);

  useEffect(() => {
    let live = true;
    for (const row of loadMyGames()) {
      api
        .getSession(row.code)
        .then((s) => patch(row.code, { name: s.title, status: s.status, playerCount: s.playerCount }))
        .catch((err: unknown) => {
          // Only a 404 means deleted; a dead server shouldn't grey out the list.
          if (err instanceof ApiError && err.status === 404) patch(row.code, { missing: true });
        });
    }

    function patch(code: string, fields: Partial<Row>) {
      if (!live) return;
      setRows((current) => current.map((r) => (r.code === code ? { ...r, ...fields } : r)));
    }

    return () => {
      live = false;
    };
  }, []);

  if (rows.length === 0) return null;


  return (
    <div className="panel col">
      <h2 className="panel-title">My games</h2>
      <ul className="game-list">
        {rows.map((game) => (
          <li key={game.code} className={game.missing ? 'is-missing' : undefined}>
            <span className="code-chip">{game.code}</span>
            <span className="col" style={{ gap: 0, minWidth: 0 }}>
              <strong>{game.name}</strong>
              <span className="faint">
                {game.missing
                  ? 'no longer on this server'
                  : `${game.role === 'created' ? 'created' : 'hosted'} by you${
                      game.playerCount === undefined
                        ? ''
                        : ` · ${game.playerCount} player${game.playerCount === 1 ? '' : 's'}`
                    }${game.status ? ` · ${game.status}` : ''}`}
              </span>
            </span>
            <span className="spacer" />
            {game.missing ? null : (
              <>
                <button className="btn btn-sm btn-primary" onClick={() => navigate(`/host/${game.code}`)}>
                  Open
                </button>
                <button className="btn btn-sm" onClick={() => navigate(`/board/${game.code}`)}>
                  TV
                </button>
              </>
            )}
            <button
              className="btn btn-sm btn-ghost"
              title="Remove from this list (the game itself is untouched)"
              onClick={() => {
                forgetGame(game.code);
                setRows((current) => current.filter((r) => r.code !== game.code));
              }}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <p className="faint" style={{ margin: 0 }}>
        Remembered on this device.
      </p>
    </div>
  );
}
